import { T, useTolgee, useTranslate } from '@tolgee/react';
import Link from 'next/link';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import useChangeLocale from '../../../hooks/useChangeLocale';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useSignOut from '../../../hooks/useSignOut';
import {
  localeOptionsForSelector,
  normalizeLocaleForSelector,
} from '../../../utils/languageSelector';
import { subjectShortName } from '../../../utils/subjectLabels';
import { SchoolPlanBadge } from '../../_atoms/SchoolPlanBadge/SchoolPlanBadge';

// ---- icons (1.8 stroke, rounded) ----
const ic = (children: ReactNode, size = 20) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);
const Globe = () =>
  ic(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>,
  );
const Info = () =>
  ic(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>,
  );
const Logout = () =>
  ic(
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>,
  );
const Building = () =>
  ic(
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h6" />
    </>,
  );
const ChevR = () => ic(<path d="m9 6 6 6-6 6" />, 18);
const Gear = () =>
  ic(
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </>,
  );
const Wrench = () =>
  ic(
    <path d="M14.5 6.5a3.5 3.5 0 0 0-4.6 4.2L3 17.6 6.4 21l6.9-6.9a3.5 3.5 0 0 0 4.2-4.6l-2.4 2.4-2.1-2.1 1.5-3.3z" />,
  );
const Check = () => ic(<path d="M20 6 9 17l-5-5" />, 18);
// Internal/back-office. A sliders glyph rather than another wrench, so it doesn't
// read as a second "Organization settings".
const Sliders = () =>
  ic(
    <>
      <path d="M4 6h16M4 12h16M4 18h16" />
      <path d="M9 4v4M15 10v4M7 16v4" />
    </>,
  );
const ChevDown = () => ic(<path d="m6 9 6 6 6-6" />, 18);
const Grad = () =>
  ic(
    <>
      <path d="M12 4 2 9l10 5 10-5-10-5z" />
      <path d="M6 11v5c0 1 3 2.5 6 2.5S18 17 18 16v-5" />
    </>,
  );
const Price = () =>
  ic(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.5 9.5a2.5 2.5 0 0 0-2.5-1.5c-1.4 0-2.5.7-2.5 2s1.1 1.7 2.5 2 2.5.7 2.5 2-1.1 2-2.5 2a2.5 2.5 0 0 1-2.5-1.5" />
      <path d="M12 6.5v11" />
    </>,
  );
const Trash = () =>
  ic(
    <>
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M9 7V4h6v3" />
    </>,
  );

const APP_VERSION = process.env.NEXT_PUBLIC_VERSION;

// Grace period for the pointer to cross the gap between a submenu's trigger and
// its flyout. Long enough for a normal diagonal sweep, short enough that the
// submenu doesn't linger once you've clearly moved on.
const SUB_CLOSE_DELAY_MS = 150;

// Pricing is deliberately absent: it graduated to a top-level item, and leaving a
// copy here would give one destination two entries in the same menu.
const HELP_LEGAL_LINKS: { label: string; href: string }[] = [
  { label: 'Help Center', href: 'https://help.curipod.com/en/' },
  { label: 'Privacy policy', href: '/privacy-policy' },
  { label: 'Terms of service', href: '/terms-of-use' },
  {
    label: 'State and District Privacy',
    href: '/c/state-and-district-privacy-agreements',
  },
  { label: 'Connectivity issues', href: '/connectivity-issues' },
];

const LEARN_MORE_LINKS: { label: string; href: string }[] = [
  { label: 'Moderation', href: '/c/how-to-moderate-inappropriate-students-in-curipod' },
  { label: 'Training and PD', href: '/c/training-and-professional-development' },
  { label: 'Impact and Efficacy', href: '/case-studies' },
  { label: 'Accessibility', href: '/accessibility' },
  { label: 'AI in Curipod', href: '/safe-ai-in-curipod' },
  { label: 'About Curipod', href: '/c/about-us' },
  { label: 'Learning principles', href: '/learning-principles' },
];

function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}

/**
 * Profile button + dropdown menu at the foot of the sidebar, in place of the old
 * flat Settings link. Grouped account first (Settings, Organization settings,
 * Trash, Language), then help (Learn More, Help & Legal), then the School plan
 * upsell, then Internal, then Log out.
 */
function SidebarProfileMenu() {
  const { t } = useTranslate();
  const tolgee = useTolgee(['language']);
  const { router, tenantId } = useRouterMatch();
  const { accessBadge } = useAccessBadge();
  const { changeLocale } = useChangeLocale();
  const { signOutAuto } = useSignOut();
  // IntercomProvider wraps the whole app in _app.tsx (CustomIntercomStateProvider),
  // so this is safe anywhere the sidebar renders.
  const { showNewMessages } = useIntercom();
  // Staff back office. Gates the Internal submenu here, and separately widens who
  // may impersonate (see checkCanImpersonate on the server).
  const isBackofficeEnabled = useFeatureFlag('IN_PRODUCT_BACKOFFICE');

  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState<
    'language' | 'learnMore' | 'helpLegal' | 'internal' | null
  >(null);
  // The menu is position:fixed so it renders on top of the UI rather than being
  // clipped by the sidebar's overflow. Anchor it to the button's rect.
  const [coords, setCoords] = useState<{ left: number; bottom: number }>({
    left: 0,
    bottom: 0,
  });
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const subCloseTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Hover opens a submenu as well as click, and moving off the pair closes it.
  //
  // Touch and pen are excluded throughout: a tap fires an enter *before* the
  // click, so acting on it would open the flyout and then let the click's toggle
  // close it again — leaving the submenus unopenable by tap. Named exclusions
  // rather than `!== 'mouse'` so anything that doesn't report a pointerType at
  // all still gets hover, which is the mouse-only case.
  const isCoarsePointer = (e: React.PointerEvent) =>
    e.pointerType === 'touch' || e.pointerType === 'pen';
  const cancelSubClose = () => {
    if (subCloseTimerRef.current) clearTimeout(subCloseTimerRef.current);
    subCloseTimerRef.current = undefined;
  };
  const openSubOnHover = (name: 'language' | 'learnMore' | 'helpLegal' | 'internal') => (
    e: React.PointerEvent,
  ) => {
    if (isCoarsePointer(e)) return;
    cancelSubClose();
    setSub(name);
  };
  // Delayed, not immediate: the flyout is anchored 8px clear of its trigger (see
  // Flyout's `left`), so the pointer crosses a gap that belongs to neither on the
  // way over. Closing on that would snatch the submenu away as you reach for it.
  // Re-entering the trigger or the flyout cancels the pending close.
  const closeSubOnLeave = (e: React.PointerEvent) => {
    if (isCoarsePointer(e)) return;
    cancelSubClose();
    subCloseTimerRef.current = setTimeout(() => setSub(null), SUB_CLOSE_DELAY_MS);
  };

  const positionMenu = () => {
    const r = buttonRef.current?.getBoundingClientRect();
    if (r) setCoords({ left: r.left, bottom: window.innerHeight - r.top + 8 });
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSub(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSub(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      // A pending hover-close would otherwise set state after unmount.
      if (subCloseTimerRef.current) clearTimeout(subCloseTimerRef.current);
    };
  }, []);

  // Keep the fixed menu anchored if the window resizes while open, and close it
  // when an ancestor scrolls. The sidebar that holds the button is
  // `overflow-y: auto`, so scrolling it moves the button while a position:fixed
  // menu stays at its old viewport coordinates — the menu visibly detaches.
  // Capture phase, because a scroll on the sidebar doesn't bubble to window.
  //
  // Scrolls that originate *inside* the menu must be ignored: the language and
  // learn-more flyouts are `overflow-y: auto` with a max height, so with enough
  // locales in the list, closing on those would dismiss the menu just as someone
  // scrolls to the language they want.
  useEffect(() => {
    if (!open) return;
    positionMenu();
    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (target && ref.current?.contains(target)) return;
      setOpen(false);
      setSub(null);
    };
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const user = accessBadge?.user;
  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.username ||
    t('Profile', 'Profile');
  const email = user?.email || '';
  const orgName = accessBadge?.organization?.name || '';
  const isAdmin = accessBadge?.role === 'Admin';
  // Nothing to sell someone who already has it.
  const isSchoolPlan =
    accessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise;
  // Students don't get the Organization settings entry (matches the profile
  // page entry points and the page's own student redirect).
  const isStudent = user?.profileInfo?.role === 'student';
  const profileInfo = user?.profileInfo;
  const firstSubject = profileInfo?.subjects?.[0];
  const subtitle =
    [
      profileInfo?.grades?.[0],
      // Texas says RLA, not ELA. Short form because this is a one-line subtitle
      // that already truncates — see subjectLabels.
      firstSubject && subjectShortName(firstSubject, accessBadge?.organization?.region),
    ]
      .filter(Boolean)
      .join(' · ') || email;

  const currentLang = normalizeLocaleForSelector(tolgee.getLanguage() || 'en-US');

  const close = () => {
    setOpen(false);
    setSub(null);
  };
  const go = (path: string) => {
    close();
    router.push(path);
  };
  // Goes through useChangeLocale rather than calling tolgee directly: the choice
  // has to reach the user's profile, or useLocale reads the stale
  // accessBadge.user.preferredLocale on the next load and reverts the language.
  const chooseLang = (value: string) => {
    changeLocale(value);
    close();
  };

  return (
    <Wrap ref={ref}>
      {open && (
        <Menu style={{ left: coords.left, bottom: coords.bottom }}>
          {email && <Email>{email}</Email>}
          {orgName && (
            <OrgCard>
              <OrgIcon>
                <Building />
              </OrgIcon>
              <OrgMain>
                <OrgName>{orgName}</OrgName>
                {accessBadge?.role && (
                  <OrgSub>
                    {isAdmin ? t('Admin', 'Admin') : t('Teacher', 'Teacher')}
                  </OrgSub>
                )}
              </OrgMain>
              <OrgCheck>
                <Check />
              </OrgCheck>
            </OrgCard>
          )}

          <MenuDivider />

          <Item onClick={() => go(`/${tenantId}/profile-settings`)}>
            <Gear />
            <span>
              <T keyName="Settings" defaultValue="Settings" />
            </span>
          </Item>
          {/* No "Account & plan" entry: everything it carried — plan and
              billing, teaching sessions, language, delete account — now lives on
              the Settings page. The old /{tenantId}/profile page stays routable
              for now and is removed in a later pass. */}
          {/* Every teacher: the General tab is where their plan and the upgrade
              path live. Matches the page's own gate, so nobody is offered a link
              that bounces them. */}
          {!isStudent && (
            <Item onClick={() => go(`/${tenantId}/organization-settings`)}>
              <Wrench />
              <span>
                <T keyName="Organization settings" defaultValue="Organization settings" />
              </span>
            </Item>
          )}
          <Item onClick={() => go(`/${tenantId}/trash`)}>
            <Trash />
            <span>
              <T keyName="Trash" defaultValue="Trash" />
            </span>
          </Item>
          {/* Grouped with Settings: it is the one place the language is changed
              now that the settings page no longer carries a picker. */}
          <FlyoutWrap onPointerEnter={cancelSubClose} onPointerLeave={closeSubOnLeave}>
            <Item
              $open={sub === 'language'}
              onClick={() => setSub(s => (s === 'language' ? null : 'language'))}
              onPointerEnter={openSubOnHover('language')}
            >
              <Globe />
              <span>
                <T keyName="Language" defaultValue="Language" />
              </span>
              <Chev>
                <ChevR />
              </Chev>
            </Item>
            {sub === 'language' && (
              <Flyout>
                {localeOptionsForSelector.map(([value, displayName]) => {
                  const selected = value === currentLang;
                  return (
                    <Item
                      key={value}
                      $selected={selected}
                      onClick={() => chooseLang(value)}
                    >
                      <span>{displayName}</span>
                      {selected && (
                        <Chev>
                          <Check />
                        </Chev>
                      )}
                    </Item>
                  );
                })}
              </Flyout>
            )}
          </FlyoutWrap>

          <MenuDivider />

          {/* Moved in here from the left bar. */}
          <FlyoutWrap onPointerEnter={cancelSubClose} onPointerLeave={closeSubOnLeave}>
            <Item
              $open={sub === 'learnMore'}
              onClick={() => setSub(s => (s === 'learnMore' ? null : 'learnMore'))}
              onPointerEnter={openSubOnHover('learnMore')}
            >
              <Grad />
              <span>
                <T keyName="Learn More" defaultValue="Learn More" />
              </span>
              <Chev>
                <ChevR />
              </Chev>
            </Item>
            {sub === 'learnMore' && (
              <Flyout>
                {LEARN_MORE_LINKS.map(l => (
                  <Link key={l.href} href={l.href} passHref>
                    <ItemAnchor target="_blank" rel="noopener noreferrer" onClick={close}>
                      <span>{t(l.label, l.label)}</span>
                    </ItemAnchor>
                  </Link>
                ))}
              </Flyout>
            )}
          </FlyoutWrap>

          <Item onClick={() => go('/pricing')}>
            <Price />
            <span>
              <T keyName="Pricing" defaultValue="Pricing" />
            </span>
          </Item>

          {/* Grouped with Learn More: both are "where do I get help" rather than
              account actions. */}
          <FlyoutWrap onPointerEnter={cancelSubClose} onPointerLeave={closeSubOnLeave}>
            <Item
              $open={sub === 'helpLegal'}
              onClick={() => setSub(s => (s === 'helpLegal' ? null : 'helpLegal'))}
              onPointerEnter={openSubOnHover('helpLegal')}
            >
              <Info />
              <span>
                <T keyName="Help & Legal" defaultValue="Help & Legal" />
              </span>
              <Chev>
                <ChevR />
              </Chev>
            </Item>
            {sub === 'helpLegal' && (
              <HelpFlyout>
                {/* Reference material first, the two actions below it: reading a
                    policy is the common errand, and an action that hands you off
                    to Intercom or a new tab is better placed where it can't be
                    hit on the way past. */}
                {HELP_LEGAL_LINKS.map(l => (
                  <Link key={l.href} href={l.href} passHref>
                    <ItemAnchor target="_blank" rel="noopener noreferrer" onClick={close}>
                      <span>{t(l.label, l.label)}</span>
                    </ItemAnchor>
                  </Link>
                ))}
                <Item
                  onClick={() => {
                    close();
                    // Prefilled so the conversation opens with context — Intercom's
                    // own launcher starts blank and most people then just say "hi".
                    showNewMessages(
                      t(
                        'sidebar-feedback-prefill',
                        "Hi! I'd like to share some feedback / report a bug: ",
                      ),
                    );
                  }}
                >
                  <span>
                    <T
                      keyName="Submit Feedback / bug report"
                      defaultValue="Submit Feedback / bug report"
                    />
                  </span>
                </Item>
                {/* New tab on purpose: the diagnostic is usually run while
                    something is going wrong mid-lesson, and a same-tab
                    navigation would throw that away. */}
                <Link href="/connectivity-issues/diagnose" passHref>
                  <ItemAnchor target="_blank" rel="noopener noreferrer" onClick={close}>
                    <span>
                      <T
                        keyName="Run Connectivity Diagnostic"
                        defaultValue="Run Connectivity Diagnostic"
                      />
                    </span>
                  </ItemAnchor>
                </Link>
                {/* Deliberately not an Item: no hover, no cursor, no click. It's a
                    label people can read out during support, not an action. */}
                <VersionLabel>
                  {t('Version {version}', 'Version {version}', {
                    version: APP_VERSION ?? 'unknown',
                  })}
                </VersionLabel>
              </HelpFlyout>
            )}
          </FlyoutWrap>

          {/* Each of these two groups carries its own divider, so a hidden group
              leaves no empty gap between rules. */}
          {!isSchoolPlan && (
            <>
              <MenuDivider />
              <Item onClick={() => go('/quotes')}>
                {/* The real School plan badge rather than a lookalike glyph, so
                    the row reads as the same tier marker teachers already see on
                    gated content. Label off — the row's own text says it. */}
                <BadgeSlot>
                  <PlanBadgeIcon showLabel={false} />
                </BadgeSlot>
                <span>
                  <T keyName="Get the School plan" defaultValue="Get the School plan" />
                </span>
              </Item>
            </>
          )}

          {/* Stays last before Log out — staff tooling, not something a teacher
              should trip over on the way to their own settings. */}
          {isBackofficeEnabled && (
            <>
              <MenuDivider />
              <FlyoutWrap
                onPointerEnter={cancelSubClose}
                onPointerLeave={closeSubOnLeave}
              >
                <Item
                  $open={sub === 'internal'}
                  onClick={() => setSub(s => (s === 'internal' ? null : 'internal'))}
                  onPointerEnter={openSubOnHover('internal')}
                >
                  <Sliders />
                  <span>
                    <T keyName="Internal" defaultValue="Internal" />
                  </span>
                  <Chev>
                    <ChevR />
                  </Chev>
                </Item>
                {sub === 'internal' && (
                  <Flyout>
                    <Item onClick={() => go('/admin/impersonate')}>
                      <span>
                        <T keyName="Impersonate User" defaultValue="Impersonate User" />
                      </span>
                    </Item>
                  </Flyout>
                )}
              </FlyoutWrap>
            </>
          )}

          <MenuDivider />

          <Item
            $danger
            onClick={() => {
              close();
              signOutAuto();
            }}
          >
            <Logout />
            <span>
              <T keyName="Log out" defaultValue="Log out" />
            </span>
          </Item>
        </Menu>
      )}

      <ProfileButton
        ref={buttonRef}
        $open={open}
        onClick={() => {
          if (!open) positionMenu();
          setOpen(o => !o);
        }}
      >
        <AvatarCircle>{initials(name)}</AvatarCircle>
        <ProfileMain>
          <ProfileName>{name}</ProfileName>
          {subtitle && <ProfileRole>{subtitle}</ProfileRole>}
        </ProfileMain>
        <Caret>
          <ChevDown />
        </Caret>
      </ProfileButton>
    </Wrap>
  );
}

export default SidebarProfileMenu;

const NAV_ACTIVE = '#F3E8D8';
const INK = theme.colors.black;

const Wrap = styled.div`
  position: relative;
  margin-top: 6px;
`;
const ProfileButton = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 9px 10px;
  border-radius: 12px;
  background: ${({ $open }) => ($open ? NAV_ACTIVE : 'transparent')};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.18s;
  &:hover {
    background: ${NAV_ACTIVE};
  }
`;
const AvatarCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: #f0cfc4;
  color: ${INK};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 15px;
  flex-shrink: 0;
`;
const ProfileMain = styled.div`
  flex: 1;
  min-width: 0;
`;
const ProfileName = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: ${INK};
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ProfileRole = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${theme.colors.greyDark};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Caret = styled.span`
  color: ${theme.colors.greyDark};
  display: inline-flex;
  flex-shrink: 0;
`;

const Menu = styled.div`
  position: fixed;
  width: 300px;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ebe2d3;
  border-radius: 16px;
  box-shadow: 0 18px 44px -10px rgba(40, 47, 51, 0.34), 0 4px 12px rgba(40, 47, 51, 0.12);
  padding: 8px;
`;
const Email = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.greyDark};
  padding: 8px 12px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const OrgCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: ${NAV_ACTIVE};
  border-radius: 10px;
  margin: 2px 0 4px;
`;
const OrgIcon = styled.span`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: #fff;
  border: 1px solid #ebe2d3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${INK};
  flex-shrink: 0;
`;
const OrgMain = styled.div`
  flex: 1;
  min-width: 0;
`;
const OrgName = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: ${INK};
  line-height: 1.15;
`;
const OrgSub = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${theme.colors.greyDark};
  margin-top: 1px;
`;
const OrgCheck = styled.span`
  color: ${theme.colors.orangeDark};
  display: inline-flex;
  flex-shrink: 0;
`;
const MenuDivider = styled.div`
  height: 1px;
  background: #f0e7da;
  margin: 6px;
`;

const itemBase = `
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  & > span:first-of-type { flex: 1; }
`;
const Item = styled.button<{ $danger?: boolean; $open?: boolean; $selected?: boolean }>`
  ${itemBase}
  color: ${({ $danger, $selected }) =>
    $danger ? theme.colors.red : $selected ? theme.colors.orangeDark : INK};
  font-weight: ${({ $selected }) => ($selected ? 900 : 700)};
  background: ${({ $open }) => ($open ? NAV_ACTIVE : 'transparent')};
  svg {
    color: ${({ $danger }) => ($danger ? theme.colors.red : theme.colors.greyDark)};
    flex-shrink: 0;
  }
  &:hover {
    background: ${({ $danger }) => ($danger ? '#FBE9E7' : NAV_ACTIVE)};
  }
`;
const ItemAnchor = styled.a`
  ${itemBase}
  color: ${INK};
  &:hover {
    background: ${NAV_ACTIVE};
  }
`;
// Even padding and a round pill: SchoolPlanBadge's compact padding is tuned for a
// crown sitting next to its label, which looks lopsided with the label off. Sized
// to sit in the same 20px slot as the menu's other icons.
// An `i` rather than a span: Item's `& > span:first-of-type { flex: 1 }` is meant
// for the label, and the badge renders a span, so as a direct child it won the
// flex slot and stretched across the row. Specificity can't fix that from the
// badge's own class — that selector is (0,2,1) — so keep the badge out of
// `span:first-of-type` reach instead. `i` is phrasing content, valid in a button.
const BadgeSlot = styled.i`
  display: inline-flex;
  flex: 0 0 auto;
`;
// Square, with even padding: SchoolPlanBadge's compact padding is tuned for a
// crown sitting next to its label, which is lopsided with the label off. 6px
// corners match the pills on the org settings page; 14px icon + 4px each side
// lands on a 22px box, the same slot as the menu's other icons.
const PlanBadgeIcon = styled(SchoolPlanBadge)`
  padding: 4px;
  border-radius: 6px;
`;
const Chev = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  color: ${theme.colors.greyDark};
`;
// Matches Item's padding so it lines up with the links above, but has none of
// its interactive affordances.
const VersionLabel = styled.div`
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 700;
  color: ${theme.colors.grey};
  cursor: default;
  user-select: text;
`;
const FlyoutWrap = styled.div`
  position: relative;
`;
const Flyout = styled.div`
  position: absolute;
  left: calc(100% + 8px);
  bottom: 0;
  min-width: 210px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ebe2d3;
  border-radius: 14px;
  box-shadow: 0 18px 44px -10px rgba(40, 47, 51, 0.34), 0 4px 12px rgba(40, 47, 51, 0.12);
  padding: 7px;
  z-index: 320;
`;
// Help & Legal outgrew the shared 320px: five links, two actions and the version
// label scrolled, which pushed the version — the one thing support asks people to
// read out — below the fold. Only this flyout is taller. The language list stays
// at 320px because it hangs off a row further down the menu, where 430px would
// reach past the top of a short window.
const HelpFlyout = styled(Flyout)`
  max-height: 430px;
`;
